import { AApi } from './AApi';
import type { RecordAccessBody } from '../types/rights.types';
import type { ApiResult, PaginationBody, PaginationParams } from '../types/api.types';
import type {
    ContactBody,
    ContactEntry,
    EntitiesRightsBody,
    EntityCreateLists,
    EmailListEntry,
    LinkedEmailBody,
    OrganizationBody,
    OrganizationDetails,
    OrganizationInput,
    OrganizationListBody,
    PersonBody,
    PersonDetails,
    PersonInput,
    PersonListBody,
    RecordRights,
    RecordTag,
    PositionBody,
    PositionInput,
} from '../types/entities.types';

/** A change (PUT/PATCH) may also make another linked address the primary one. */
type WithPrimaryEmail<TInput> = TInput & { primary_email_uuid?: string };

/**
 * The Entities module: `/persons` and `/organizations` — plain REST (`replace…` = PUT, `update…` = PATCH) plus the
 * links to their addresses (`/…/{id}/emails`). The addresses themselves are `ApiService.emails`.
 */
export class EntitiesApi extends AApi {
    /** One transaction: without the right for all of it nothing stays. */
    public createOrganization(data: OrganizationInput, lists: EntityCreateLists): Promise<ApiResult<{ organization: OrganizationBody }>> {
        return this.Client.post(this.organizations(), { ...data, ...lists });
    }

    /** One transaction: without the right for all of it nothing stays. */
    public createPerson(data: PersonInput, lists: EntityCreateLists): Promise<ApiResult<{ person: PersonBody }>> {
        return this.Client.post(this.persons(), { ...data, ...lists });
    }

    public deleteOrganization(organizationId: number): Promise<ApiResult<undefined>> {
        return this.Client.delete(this.organizations(organizationId));
    }

    public deletePerson(personId: number): Promise<ApiResult<undefined>> {
        return this.Client.delete(this.persons(personId));
    }

    /** For admins: who reaches the record on which level. */
    public getRecordAccess(resource: 'person' | 'organization', id: number): Promise<ApiResult<RecordAccessBody>> {
        return this.Client.get(`${resource === 'person' ? this.persons(id) : this.organizations(id)}/rights`);
    }

    /** What the user can create in this module; `EntitiesRights` keeps it for five minutes. */
    public getRights(): Promise<ApiResult<EntitiesRightsBody>> {
        return this.Client.get(`${this.VersionPath}/entities/rights`);
    }

    public getOrganization(organizationId: number): Promise<ApiResult<OrganizationDetails>> {
        return this.Client.get(this.organizations(organizationId));
    }

    public getPerson(personId: number): Promise<ApiResult<PersonDetails>> {
        return this.Client.get(this.persons(personId));
    }

    public listOrganizationEmails(organizationId: number): Promise<ApiResult<{ emails: LinkedEmailBody[] }>> {
        return this.Client.get(`${this.organizations(organizationId)}/emails`);
    }

    public listOrganizations(params?: PaginationParams): Promise<ApiResult<{ organizations: OrganizationListBody[]; pagination?: PaginationBody }>> {
        return this.Client.get(`${this.organizations()}${this.toQuery(params)}`);
    }

    public listPersonEmails(personId: number): Promise<ApiResult<{ emails: LinkedEmailBody[] }>> {
        return this.Client.get(`${this.persons(personId)}/emails`);
    }

    public listPersons(params?: PaginationParams): Promise<ApiResult<{ persons: PersonListBody[]; pagination?: PaginationBody }>> {
        return this.Client.get(`${this.persons()}${this.toQuery(params)}`);
    }

    public replaceOrganization(organizationId: number, data: WithPrimaryEmail<OrganizationInput>): Promise<ApiResult<{ organization: OrganizationBody }>> {
        return this.Client.put(this.organizations(organizationId), data);
    }

    public replacePerson(personId: number, data: WithPrimaryEmail<PersonInput>): Promise<ApiResult<{ person: PersonBody }>> {
        return this.Client.put(this.persons(personId), data);
    }

    public unlinkOrganizationEmail(organizationId: number, emailUuid: string): Promise<ApiResult<undefined>> {
        return this.Client.delete(`${this.organizations(organizationId)}/emails/${encodeURIComponent(emailUuid)}`);
    }

    public unlinkPersonEmail(personId: number, emailUuid: string): Promise<ApiResult<undefined>> {
        return this.Client.delete(`${this.persons(personId)}/emails/${encodeURIComponent(emailUuid)}`);
    }

    public createPosition(data: PositionInput): Promise<ApiResult<{ position: PositionBody }>> {
        return this.Client.post(`${this.VersionPath}/positions`, data);
    }

    /** Refused (`POSITION.used`) while a contact holds it. */
    public deletePosition(positionId: number): Promise<ApiResult<undefined>> {
        return this.Client.delete(`${this.VersionPath}/positions/${positionId.toString()}`);
    }

    public getPosition(positionId: number): Promise<ApiResult<{ position: PositionBody }>> {
        return this.Client.get(`${this.VersionPath}/positions/${positionId.toString()}`);
    }

    public listPositions(params?: PaginationParams): Promise<ApiResult<{ positions: PositionBody[]; pagination?: PaginationBody }>> {
        return this.Client.get(`${this.VersionPath}/positions${this.toQuery(params)}`);
    }

    public reorderPositions(order: number[]): Promise<ApiResult<{ positions: PositionBody[] }>> {
        return this.Client.put(`${this.VersionPath}/positions/sort`, { order });
    }

    public sortPosition(positionId: number, sort: number): Promise<ApiResult<{ positions: PositionBody[] }>> {
        return this.Client.put(`${this.VersionPath}/positions/sort`, { position_id: positionId, sort });
    }

    public replaceOrganizationTags(organizationId: number, tags: string[]): Promise<ApiResult<{ tags: RecordTag[]; rights: RecordRights }>> {
        return this.Client.put(`${this.organizations(organizationId)}/tags`, { tags });
    }

    public replacePersonTags(personId: number, tags: string[]): Promise<ApiResult<{ tags: RecordTag[]; rights: RecordRights }>> {
        return this.Client.put(`${this.persons(personId)}/tags`, { tags });
    }

    /** The complete contact list of an organization; each entry with `person_id`. */
    public replaceOrganizationContacts(organizationId: number, contacts: ContactEntry[]): Promise<ApiResult<{ contacts: ContactBody[] }>> {
        return this.Client.put(`${this.organizations(organizationId)}/contacts`, { contacts });
    }

    /** The complete organization list of a person; each entry with `organization_id`. */
    public replacePersonContacts(personId: number, contacts: ContactEntry[]): Promise<ApiResult<{ contacts: ContactBody[] }>> {
        return this.Client.put(`${this.persons(personId)}/contacts`, { contacts });
    }

    public updatePosition(positionId: number, data: PositionInput): Promise<ApiResult<{ position: PositionBody }>> {
        return this.Client.patch(`${this.VersionPath}/positions/${positionId.toString()}`, data);
    }

    /** The complete address list; without an `is_primary` one the newest link becomes primary. */
    public replaceOrganizationEmails(organizationId: number, emails: EmailListEntry[]): Promise<ApiResult<{ emails: LinkedEmailBody[] }>> {
        return this.Client.put(`${this.organizations(organizationId)}/emails`, { emails });
    }

    /** The complete address list; the login address of the person's user must stay. */
    public replacePersonEmails(personId: number, emails: EmailListEntry[]): Promise<ApiResult<{ emails: LinkedEmailBody[] }>> {
        return this.Client.put(`${this.persons(personId)}/emails`, { emails });
    }

    public updateOrganization(organizationId: number, data: WithPrimaryEmail<OrganizationInput>): Promise<ApiResult<{ organization: OrganizationBody }>> {
        return this.Client.patch(this.organizations(organizationId), data);
    }

    public updatePerson(personId: number, data: WithPrimaryEmail<PersonInput>): Promise<ApiResult<{ person: PersonBody }>> {
        return this.Client.patch(this.persons(personId), data);
    }

    private organizations(organizationId?: number): string {
        return `${this.VersionPath}/organizations${organizationId === undefined ? '' : `/${organizationId.toString()}`}`;
    }

    private persons(personId?: number): string {
        return `${this.VersionPath}/persons${personId === undefined ? '' : `/${personId.toString()}`}`;
    }
}
